import { Box, styled } from '@mui/material';
import type { Tone } from '../types/dashboard';
import { toneColors } from './Common.styles';

/**
 * Rounded-square skill emblem. Sized by props so the same tile serves the
 * 48px recently-viewed card, the 44px browse row and the 40px trending row.
 *
 * `hasLogo` switches the plinth to a neutral surface. Skill logos are full
 * colour (Python's blue-and-yellow, React's cyan, Git's orange), and a tinted
 * plate behind them muddies both — the tint only earns its place behind a
 * monogram, where it is the entire colour of the tile.
 */
export const TileRoot = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'tone' && prop !== 'size' && prop !== 'radius',
})<{ tone: Tone; size: number; radius: number }>(({
  theme,
  tone,
  size,
  radius,
}) => {
  const colors = toneColors(theme, tone);
  return {
    width: size,
    height: size,
    borderRadius: radius,
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    transition: 'border-color .15s, box-shadow .15s',
    background: theme.palette.background.paper,
    // Default tone keeps the neutral outline; a named tone tints the edge, which
    // is how the trending leader is marked without recolouring the whole plate.
    border: `1.5px solid ${
      tone === 'blue' ? theme.palette.guidedCourse.line : colors.border
    }`,
    boxShadow: `inset 0 -2px 0 ${theme.alpha(
      tone === 'blue' ? theme.palette.guidedCourse.line : colors.border,
      0.7,
    )}`,
  };
});

export const TileImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'placeholder',
})<{ placeholder?: boolean }>(({ placeholder }) => ({
  width: placeholder ? '58%' : '74%',
  height: placeholder ? '58%' : '74%',
  objectFit: 'contain',
}));

/** Circular initial avatar with the design's white ring and drop lip. */
export const AvatarRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'bg',
})<{ size: number; bg: string }>(({ theme, size, bg }) => ({
  width: size,
  height: size,
  borderRadius: 999,
  flexShrink: 0,
  display: 'grid',
  placeItems: 'center',
  overflow: 'hidden',
  fontSize: size * 0.42,
  fontWeight: 900,
  lineHeight: 1,
  color: '#fff',
  background: bg,
  border: `2px solid ${theme.palette.background.paper}`,
  boxShadow: theme.palette.guidedCourse.shadowSm,
}));

export const AvatarImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});
